import { CollapsibleDiv, Loader, Pill, TextArea, Typography } from '@recruit-robin/robin-theme/web-components';
import { Star_06 } from '@recruit-robin/robin-theme/web-icons';
import { useCallback, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCurrentEnrichedFormValues } from '../../../../hooks/useCurrentEnrichedFormValues';
import { useJobFieldSmartWriter } from '../../../../hooks/useJobFieldSmartWriter';
import { JobFormValues } from '../../EditEnrichedVacancy.types';
import { useStyles } from './FormSections.styles';
import { RenderField } from './RenderField';
import { useQualityStatus } from './useQualityStatus';

export const PositionOverview = () => {
  const FIELD_NAME = 'position_overview';
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);

  const fieldConfig = {
    weight: 100,
  };

  const { mutateAsync: smartWrite, isLoading } = useJobFieldSmartWriter();
  const { watch, setValue } = useFormContext<JobFormValues>();
  const getCurrentValues = useCurrentEnrichedFormValues();
  const currentFormValues = getCurrentValues({ sanitize: true });
  const currentFieldValue = watch(FIELD_NAME);
  const { statusConfig } = useQualityStatus<JobFormValues>(FIELD_NAME, fieldConfig);

  const handleEnhanceField = useCallback(async () => {
    if (isLoading) {
      return;
    }
    const response = await smartWrite({
      field_name: FIELD_NAME,
      field_value: currentFieldValue,
      job_data: currentFormValues,
    });

    const newText = response?.result ?? '';

    setValue(FIELD_NAME, newText, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [currentFormValues, currentFieldValue, setValue, smartWrite, isLoading]);

  return (
    <CollapsibleDiv
      initialOpen
      limitToggleToTitle
      key={FIELD_NAME}
      title={
        <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
          {dictionary.positionOverview}
        </Typography>
      }
      endAdornment={
        <>
          <Pill
            startIcon={isLoading ? <Loader variant="typing-1" size={16} /> : <Star_06 size={16} />}
            label={isLoading ? dictionary.thinking : dictionary.improveWithAI}
            style="icon-removable"
            type="ai"
            onClick={handleEnhanceField}
          />
          <Pill
            startIcon={statusConfig.icon}
            label={statusConfig.label}
            style="icon-removable"
            type={statusConfig.type}
            padding="text"
          />
        </>
      }
      customCss={classes.section}>
      <div css={classes.sectionContent}>
        <RenderField
          fieldName={FIELD_NAME}
          render={(controllerArg) => (
            <TextArea
              value={controllerArg.value || ''}
              onChange={controllerArg.onChange}
              onBlur={controllerArg.onBlur}
              placeholder={dictionary.jobDescription}
              rows={3}
              autoGrow
              disabled={isLoading}
            />
          )}
        />
      </div>
    </CollapsibleDiv>
  );
};
